/* OnSumn v2 — Glass Hero with Split-Flap Display */

/* ── Bay Area Pro Sports — official brand hex. Trailing periods retired
   2026-08-20 (Michael) — keep in lockstep with lib/hero-flap.js TEAMS. ── */
const BAY_TEAMS = [
  { name: 'OnSumn',    bg: '#FF4F00', color: '#FFFFFF', glow: '0 0 6px rgba(255,255,255,0.3)' },
  { name: '49ers',     bg: '#AA0000', color: '#B3995D', glow: '0 0 6px rgba(179,153,93,0.4)' },
  { name: 'Giants',    bg: '#27251F', color: '#FD5A1E', glow: '0 0 8px rgba(253,90,30,0.4)' },
  { name: 'Athletics', bg: '#003831', color: '#EFB21E', glow: '0 0 6px rgba(239,178,30,0.4)' },
  { name: 'Warriors',  bg: '#1D428A', color: '#FFC72C', glow: '0 0 6px rgba(255,199,44,0.4)' },
  { name: 'Valkyries', bg: '#4833B3', color: '#F3EEE3', glow: '0 0 8px rgba(72,51,179,0.5)' },
  { name: 'Sharks',    bg: '#006D75', color: '#FFFFFF', glow: '0 0 6px rgba(255,255,255,0.3)' },
  { name: 'Quakes',    bg: '#003DA6', color: '#FFFFFF', glow: '0 0 6px rgba(255,255,255,0.3)' },
  { name: 'Bay FC',    bg: '#006FB9', color: '#FFFFFF', glow: '0 0 6px rgba(255,255,255,0.3)' },
  { name: 'Roots',     bg: '#00573F', color: '#E3A82B', glow: '0 0 6px rgba(227,168,43,0.4)' },
];

const MAX_FLAP_LEN = Math.max(...BAY_TEAMS.map(t => t.name.length));

/* ── Single flap character cell ── */
function FlapChar({ char, position, color, glow, bg }) {
  const [display, setDisplay] = React.useState(char);
  const [displayColor, setDisplayColor] = React.useState(color);
  const [displayGlow, setDisplayGlow] = React.useState(glow);
  const [displayBg, setDisplayBg] = React.useState(bg);
  const [phase, setPhase] = React.useState('idle');
  const prev = React.useRef(char);

  React.useEffect(() => {
    if (char !== prev.current) {
      const d = position * 45;
      const t1 = setTimeout(() => setPhase('out'), d);
      const t2 = setTimeout(() => {
        setDisplay(char);
        setDisplayColor(color);
        setDisplayGlow(glow);
        setDisplayBg(bg);
        setPhase('in');
      }, d + 130);
      const t3 = setTimeout(() => { setPhase('idle'); prev.current = char; }, d + 300);
      return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    } else {
      setDisplayColor(color);
      setDisplayGlow(glow);
      setDisplayBg(bg);
    }
  }, [char, position, color, glow, bg]);

  const targetIsSpace = char.trim() === '';
  const isSpace = display.trim() === '';

  return React.createElement('span', {
    className: 'flap-cell',
    style: {
      display: 'inline-block',
      position: 'relative',
      width: targetIsSpace ? '0.4em' : char === '.' ? '0.45em' : (char === 'W' || char === 'M') ? '1.08em' : '0.88em',
      height: '1.2em',
      lineHeight: '1.2em',
      textAlign: 'center',
      fontStyle: 'normal',
      fontWeight: 800,
      background: targetIsSpace ? 'transparent' : displayBg,
      color: displayColor,
      textShadow: isSpace ? 'none' : displayGlow,
      borderRadius: 4,
      margin: '0 clamp(1px, 0.4vw, 3px)',
      transform: phase === 'out' ? 'perspective(400px) rotateX(-90deg)'
               : phase === 'in'  ? 'perspective(400px) rotateX(0deg)'
               : 'none',
      transition: phase === 'idle' ? 'background 0.4s, color 0.4s, text-shadow 0.4s' : 'transform 0.13s ease-in-out',
      transformOrigin: '50% 50%',
      boxShadow: isSpace ? 'none' : '0 2px 6px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)',
      overflow: 'hidden',
    },
  },
    display,
    /* Split line — the signature Solari board look */
    !isSpace && React.createElement('span', {
      style: {
        position: 'absolute', left: 0, right: 0, top: '50%',
        height: 1,
        background: 'rgba(0,0,0,0.25)',
        pointerEvents: 'none',
      },
    })
  );
}

/* ── Split-flap display — cycles through team names with brand colors ── */
function SplitFlapDisplay({ darkMode }) {
  const [index, setIndex] = React.useState(0);
  const containerRef = React.useRef(null);
  // Shared hook (lib/use-reduced-motion.js). Freezes the flap on the first
  // team when user has prefers-reduced-motion: reduce. Re-renders when the
  // OS preference toggles mid-session — effect re-runs via dep array.
  const reducedMotion = typeof window !== 'undefined' && typeof window.useReducedMotion === 'function'
    ? window.useReducedMotion()
    : false;

  React.useEffect(() => {
    if (reducedMotion) return;
    let timer = null;
    let onScreen = true;
    const tick = () => setIndex(i => (i + 1) % BAY_TEAMS.length);
    const start = () => { if (!timer) timer = setInterval(tick, 2800); };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    const sync = () => { (document.hidden || !onScreen) ? stop() : start(); };

    document.addEventListener('visibilitychange', sync);
    let obs = null;
    if (containerRef.current && 'IntersectionObserver' in window) {
      obs = new IntersectionObserver(([entry]) => {
        onScreen = entry.isIntersecting;
        sync();
      }, { threshold: 0 });
      obs.observe(containerRef.current);
    }
    start();
    return () => {
      stop();
      document.removeEventListener('visibilitychange', sync);
      if (obs) obs.disconnect();
    };
  }, [reducedMotion]);

  const team = BAY_TEAMS[index];
  const padded = team.name.padEnd(MAX_FLAP_LEN);

  // a11y: announce the team name as one label (atomic, polite live region)
  // rather than letting AT read each flap cell's letter stream.
  return (
    <span
      ref={containerRef}
      aria-label={team.name}
      aria-live="polite"
      aria-atomic="true"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        padding: 'clamp(4px, 0.8vw, 6px) clamp(6px, 1vw, 10px) clamp(5px, 0.8vw, 8px)',
        borderRadius: 8,
        background: darkMode ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.06)',
        verticalAlign: 'baseline',
        fontSize: 'clamp(24px, 5vw, inherit)',
      }}
    >
      {padded.split('').map((c, i) => (
        <span key={i} aria-hidden="true" style={{ display: 'inline-block' }}>
          <FlapChar
            char={c}
            position={i}
            color={team.color}
            glow={team.glow}
            bg={team.bg}
          />
        </span>
      ))}
    </span>
  );
}

/* ── Hero Section ──
 *
 * Issue #44 "phantom LCP" fix (Approach 1 / static-first hero):
 *
 * On index.html we ship a SSR static hero outside #root (see index.html
 * comment near the <section class="static-hero"> markup). That static
 * <p class="static-hero__sub"> paints at FCP (~2.3s on mobile) and is
 * the LCP element. If React's HeroSection also rendered hero markup,
 * createRoot would mount a *different-shaped* <p> on top and Lighthouse
 * would treat it as a NEW LCP candidate — resetting the clock to ~7.3s
 * when React finishes (the "phantom LCP" autopsy Jace traced).
 *
 * Fix: when the static hero is in the DOM, HeroSection returns null.
 * The static hero stays put forever; the LCP element identity never
 * changes; Lighthouse credits FCP as LCP. The React app still mounts
 * everything else (Header, VenueRail, Events, Map, Footer, modals).
 *
 * Tradeoffs (accepted for the LCP win):
 *  - SplitFlapDisplay animation (Bay sports team rotation) doesn't play
 *    on the homepage. Static hero shows the "OnSumn" pill permanently.
 *    Same visual as the first React render before PR #52 ever shipped.
 *  - Stat line ("1,080 events across 9 cities · Updated today") doesn't
 *    appear on the homepage hero. Counts still surface in the events
 *    section below the fold.
 *
 * Fallback path (non-SSR contexts): if no [data-static-hero] is in the
 * DOM, we render the original full React hero — preserves the split-flap
 * + stat line for any future page that mounts <HeroSection> directly.
 */
function HeroSection({ darkMode, radius, eventCount, cityCount, lastUpdated, updatedRecently }) {
  // If the SSR static hero is already painted, leave it alone — it's the LCP.
  // Guarded for SSR / test environments where `document` is undefined.
  if (typeof document !== 'undefined' && document.querySelector('[data-static-hero]')) {
    return null;
  }

  const textColor = darkMode ? '#fff' : '#0A0A0A';

  return (
    <section data-hero-section style={{
      position: 'relative', minHeight: '94vh',
      display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
      padding: '120px 32px 80px', textAlign: 'center',
      background: darkMode
        ? 'radial-gradient(ellipse at 30% 20%, rgba(213,120,0,0.08) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(255,79,0,0.05) 0%, transparent 50%), #0A0A0A'
        : 'radial-gradient(ellipse at 30% 20%, rgba(213,120,0,0.06) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(255,79,0,0.04) 0%, transparent 50%), #F5F5F5',
      overflow: 'hidden',
    }}>
      {/* Ambient glow orbs — .hero-orb class lets index.html @media dim to 30% on ≤768 */}
      <div className="hero-orb" style={{
        position: 'absolute', top: '-10%', left: '-5%',
        width: 400, height: 400, borderRadius: '50%',
        background: darkMode ? 'rgba(206,128,73,0.12)' : 'rgba(206,128,73,0.08)',
        filter: 'blur(100px)',
        mixBlendMode: darkMode ? 'color-dodge' : 'multiply',
      }} />
      <div className="hero-orb" style={{
        position: 'absolute', bottom: '5%', right: '-5%',
        width: 350, height: 350, borderRadius: '50%',
        background: darkMode ? 'rgba(255,79,0,0.08)' : 'rgba(255,79,0,0.05)',
        filter: 'blur(120px)',
        mixBlendMode: darkMode ? 'color-dodge' : 'multiply',
      }} />

      <div style={{ position: 'relative', zIndex: 2, maxWidth: 780 }}>
        {/* Ticker in glass pill */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 10,
          ...glassStyle(darkMode, 'btn'),
          border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
          borderRadius: 100,
          padding: '8px 20px', marginBottom: 32,
        }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%',
            background: 'var(--international-orange)',
            boxShadow: '0 0 8px var(--international-orange)',
          }} />
          <span style={{
            fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            color: darkMode ? 'var(--amber-gold-light)' : 'var(--amber-gold-ink)',
          }}>LIVE EVENTS · CULTURE · BAY AREA</span>
        </div>

        {/* Headline with split-flap */}
        <h1 style={{
          fontFamily: 'var(--font-display)', fontSize: 'clamp(32px, 6.5vw, 80px)',
          fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.03em',
          color: textColor, marginBottom: 28,
          textTransform: 'uppercase',
        }}>
          <span>The Bay Stay</span>
          <br />
          <SplitFlapDisplay darkMode={darkMode} />
        </h1>

        {/* Sub — increased contrast in light mode. The freshness claim is
            EARNED, not asserted (Q4, 2026-07-28): "updated daily" renders
            only while the events.json build stamp is under 36h old
            (updatedRecently, computed in index-main). Stale or UNKNOWN age
            drops the claim — a hardcoded "updated daily" would keep lying
            if the scrapers broke. 36h not 24h: the claim survives one cron
            hiccup window, dies before day two. The stat line below was
            always honest ("Updated {lastUpdated}") — this brings the sub
            up to its standard. Gated: tests/hero-freshness.test.js. */}
        <p style={{
          fontFamily: 'var(--font-body)', fontSize: 18, fontWeight: 400,
          lineHeight: '28px',
          color: darkMode ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.72)',
          maxWidth: 520, margin: '0 auto 40px',
        }}>
          {updatedRecently === true
            ? 'Everything worth pulling up to. Music, art, food, cards, nightlife — all Bay, updated daily.'
            : 'Everything worth pulling up to. Music, art, food, cards, nightlife — all Bay.'}
        </p>

        {/* Inline stat line */}
        {eventCount > 0 && (
          <div style={{ marginTop: 24 }}>
            <span style={{
              fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600,
              color: darkMode ? 'var(--amber-gold-light)' : 'var(--amber-gold-ink)',
            }}>{eventCount.toLocaleString()} events across {cityCount} cities · Updated {lastUpdated || 'today'}</span>
          </div>
        )}
      </div>

      {/* Scroll hint */}
      <div style={{
        position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
        animation: 'heroFloat 2.5s ease-in-out infinite',
      }}>
        <div style={{
          fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600,
          letterSpacing: '0.1em', textTransform: 'uppercase',
          color: darkMode ? 'rgba(255,255,255,0.62)' : 'rgba(0,0,0,0.58)',
        }}>WHAT'S GOOD</div>
        <div style={{
          width: 1, height: 20,
          background: darkMode ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)',
        }} />
      </div>
    </section>
  );
}
HeroSection.propTypes = {
  darkMode: PropTypes.bool.isRequired,
  radius: PropTypes.number.isRequired,
  eventCount: PropTypes.number,
  cityCount: PropTypes.number,
  lastUpdated: PropTypes.string,
  updatedRecently: PropTypes.bool,
};

window.HeroSection = HeroSection;
